import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import apiClient from "../api/apiClient";

interface ISettingsState {
  emailNotificationsEnabled: boolean;
  questionnaireEmailsEnabled: boolean;
  contactEmailsEnabled?: boolean;
  contactVisitorReceiptsEnabled: boolean;
  contactDailyDigestEnabled: boolean;
  updatedAt?: string;
}

export default function AdminSettings() {
  const { t } = useTranslation();

  const [settings, setSettings] = useState<ISettingsState>({
    emailNotificationsEnabled: true,
    questionnaireEmailsEnabled: true,
    contactVisitorReceiptsEnabled: true,
    contactDailyDigestEnabled: true,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  // Fetch initial settings from backend
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/api/v1/settings");
      if (res.data?.data) {
        setSettings({
          emailNotificationsEnabled: res.data.data.emailNotificationsEnabled ?? true,
          questionnaireEmailsEnabled: res.data.data.questionnaireEmailsEnabled ?? true,
          contactEmailsEnabled: res.data.data.contactEmailsEnabled ?? true,
          contactVisitorReceiptsEnabled: res.data.data.contactVisitorReceiptsEnabled ?? true,
          contactDailyDigestEnabled: res.data.data.contactDailyDigestEnabled ?? true,
          updatedAt: res.data.data.updatedAt,
        });
      }
    } catch (error: any) {
      console.error("Error fetching settings:", error);
      toast.error(
        error.response?.data?.error?.message ||
          t("settings.fetchError", "Failed to load system settings")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key: keyof ISettingsState) => {
    const previousState = { ...settings };
    const updatedValue = !settings[key];
    const newSettings = { ...settings, [key]: updatedValue };

    // Optimistic UI update
    setSettings(newSettings);

    try {
      setSaving(true);
      const res = await apiClient.put("/api/v1/settings", {
        [key]: updatedValue,
      });

      if (res.data?.data) {
        setSettings((prev) => ({
          ...prev,
          ...res.data.data,
        }));
        toast.success(
          t(
            "settings.updatedSuccess",
            "Settings updated successfully"
          )
        );
      }
    } catch (error: any) {
      console.error("Error updating setting:", error);
      // Revert optimistic update on failure
      setSettings(previousState);
      toast.error(
        error.response?.data?.error?.message ||
          t("settings.updateError", "Failed to save setting")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testEmail || !testEmail.includes("@")) {
      toast.warn(t("settings.validEmailWarning", "Please enter a valid email address"));
      return;
    }

    try {
      setSendingTest(true);
      const res = await apiClient.post("/api/v1/settings/test-email", {
        recipientEmail: testEmail.trim(),
      });
      toast.success(
        res.data?.message ||
          t("settings.testEmailSuccess", `Test email sent to ${testEmail}`)
      );
    } catch (error: any) {
      console.error("Error sending test email:", error);
      toast.error(
        error.response?.data?.error?.message ||
          t("settings.testEmailError", "Failed to send test email. Check SMTP settings.")
      );
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 max-w-5xl">
      {/* Page Header */}
      <div className="py-1 pb-4 sm:pb-6 shrink-0">
        <h2 className="text-xl sm:text-2xl font-gilroy font-semibold text-[#041B44]">
          {t("settings.title", "System Settings")}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {t(
            "settings.subtitle",
            "Manage global email notifications, automated questionnaire alerts, and delivery preferences."
          )}
        </p>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl p-12 shadow-xs border border-gray-100 flex flex-col items-center justify-center">
          <div className="w-10 h-10 border-4 border-[#037897] border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-sm font-semibold text-gray-600">
            {t("settings.loading", "Loading settings...")}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Card 1: Master Email Notification Switch */}
          <div
            className={`rounded-2xl p-5 sm:p-8 border shadow-xs ${
              settings.emailNotificationsEnabled
                ? "bg-white border-[#037897]/20"
                : "bg-[#FFF9F9] border-red-200"
            }`}
          >
            <div className="flex flex-row items-center justify-between gap-3 sm:gap-4">
              <div className="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0 flex-1">
                <div
                  className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-xl sm:text-2xl shrink-0 transition-colors ${
                    settings.emailNotificationsEnabled
                      ? "bg-[#EBF7F9] text-[#037897]"
                      : "bg-red-50 text-red-500"
                  }`}
                >
                  {settings.emailNotificationsEnabled ? "🔔" : "🔕"}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <h3 className="text-base sm:text-lg font-bold text-[#041B44]">
                      {t(
                        "settings.masterEmailTitle",
                        "Master Email Notifications"
                      )}
                    </h3>
                    <span
                      className={`text-[10px] sm:text-xs font-bold px-2 sm:px-2.5 py-0.5 rounded-full ${
                        settings.emailNotificationsEnabled
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-red-100 text-red-700 border border-red-200"
                      }`}
                    >
                      {settings.emailNotificationsEnabled
                        ? t("settings.active", "ACTIVE")
                        : t("settings.paused", "PAUSED")}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1 max-w-2xl leading-relaxed">
                    {t(
                      "settings.masterEmailDesc",
                      "Turn this switch ON or OFF to control all outgoing automated notification emails across the Centre SEVA platform."
                    )}
                  </p>
                </div>
              </div>

              {/* Master Toggle Switch */}
              <div className="flex items-center shrink-0 self-center">
                <button
                  type="button"
                  role="switch"
                  aria-checked={settings.emailNotificationsEnabled}
                  disabled={saving}
                  onClick={() => handleToggle("emailNotificationsEnabled")}
                  className={`relative inline-flex h-7 w-14 sm:h-8 sm:w-16 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-[#037897] focus:ring-offset-2 ${
                    settings.emailNotificationsEnabled
                      ? "bg-[#037897]"
                      : "bg-gray-300"
                  } ${saving ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <span className="sr-only">{t("settings.toggleEmailNotifications", "Toggle Email Notifications")}</span>
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-6 w-6 sm:h-7 sm:w-7 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                      settings.emailNotificationsEnabled
                        ? "translate-x-7 sm:translate-x-8"
                        : "translate-x-0"
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Card 2: Granular Email Notification Channels */}
          <div className="bg-white rounded-2xl p-5 sm:p-8 border border-gray-100 shadow-xs">
            <h3 className="text-base sm:text-lg font-bold text-[#041B44] mb-1">
              {t("settings.channelsTitle", "Notification Channels")}
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 mb-6">
              {t(
                "settings.channelsSubtitle",
                "Customize specific types of automated emails sent to users and administrators."
              )}
            </p>

            <div className="divide-y divide-gray-100">
              {/* Channel 1: Questionnaire Assignment Alerts */}
              <div className="py-3.5 sm:py-4.5 flex flex-row items-center justify-between gap-3 sm:gap-4">
                <div className="flex items-start gap-3 sm:gap-3.5 min-w-0 flex-1">
                  <span className="text-lg sm:text-xl mt-0.5 shrink-0">📋</span>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-xs sm:text-sm font-bold text-[#041B44]">
                      {t(
                        "settings.questionnaireAlertsTitle",
                        "Questionnaire Availability Alerts"
                      )}
                    </h4>
                    <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1 max-w-xl leading-relaxed">
                      {t(
                        "settings.questionnaireAlertsDesc",
                        "Send email notifications to enrolled patients whenever new daily or weekly questionnaires are unlocked."
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-center shrink-0 self-center">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={settings.questionnaireEmailsEnabled}
                    disabled={!settings.emailNotificationsEnabled || saving}
                    onClick={() => handleToggle("questionnaireEmailsEnabled")}
                    className={`relative inline-flex h-6 w-12 sm:h-7 sm:w-14 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden ${
                      settings.questionnaireEmailsEnabled &&
                      settings.emailNotificationsEnabled
                        ? "bg-[#037897] cursor-pointer"
                        : "bg-gray-200 cursor-not-allowed"
                    }`}
                  >
                    <span className="sr-only">{t("settings.toggleQuestionnaireEmails", "Toggle Questionnaire Emails")}</span>
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 sm:h-6 sm:w-6 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                        settings.questionnaireEmailsEnabled &&
                        settings.emailNotificationsEnabled
                          ? "translate-x-6 sm:translate-x-7"
                          : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Channel 2: Visitor Confirmation Receipts */}
              <div className="py-3.5 sm:py-4.5 flex flex-row items-center justify-between gap-3 sm:gap-4">
                <div className="flex items-start gap-3 sm:gap-3.5 min-w-0 flex-1">
                  <span className="text-lg sm:text-xl mt-0.5 shrink-0">📨</span>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-xs sm:text-sm font-bold text-[#041B44]">
                      {t("settings.visitorReceiptsTitle", "Visitor Confirmation Receipts")}
                    </h4>
                    <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1 max-w-xl leading-relaxed">
                      {t(
                        "settings.visitorReceiptsDesc",
                        "Send an automated confirmation receipt to visitors immediately after they submit a message via the public Contact form."
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-center shrink-0 self-center">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={settings.contactVisitorReceiptsEnabled}
                    disabled={!settings.emailNotificationsEnabled || saving}
                    onClick={() => handleToggle("contactVisitorReceiptsEnabled")}
                    className={`relative inline-flex h-6 w-12 sm:h-7 sm:w-14 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden ${
                      settings.contactVisitorReceiptsEnabled &&
                      settings.emailNotificationsEnabled
                        ? "bg-[#037897] cursor-pointer"
                        : "bg-gray-200 cursor-not-allowed"
                    }`}
                  >
                    <span className="sr-only">{t("settings.toggleReceipts", "Toggle Visitor Confirmation Receipts")}</span>
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 sm:h-6 sm:w-6 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                        settings.contactVisitorReceiptsEnabled &&
                        settings.emailNotificationsEnabled
                          ? "translate-x-6 sm:translate-x-7"
                          : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Channel 3: Admin Daily Inquiries Digest */}
              <div className="py-3.5 sm:py-4.5 flex flex-row items-center justify-between gap-3 sm:gap-4">
                <div className="flex items-start gap-3 sm:gap-3.5 min-w-0 flex-1">
                  <span className="text-lg sm:text-xl mt-0.5 shrink-0">📊</span>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-xs sm:text-sm font-bold text-[#041B44]">
                      {t("settings.dailyDigestTitle", "Admin Daily Inquiries Digest")}
                    </h4>
                    <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1 max-w-xl leading-relaxed">
                      {t(
                        "settings.dailyDigestDesc",
                        "Send an end-of-day summary email at 8:00 PM to the administrator when new contact inquiries have been received."
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-center shrink-0 self-center">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={settings.contactDailyDigestEnabled}
                    disabled={!settings.emailNotificationsEnabled || saving}
                    onClick={() => handleToggle("contactDailyDigestEnabled")}
                    className={`relative inline-flex h-6 w-12 sm:h-7 sm:w-14 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden ${
                      settings.contactDailyDigestEnabled &&
                      settings.emailNotificationsEnabled
                        ? "bg-[#037897] cursor-pointer"
                        : "bg-gray-200 cursor-not-allowed"
                    }`}
                  >
                    <span className="sr-only">{t("settings.toggleDigest", "Toggle Admin Daily Digest")}</span>
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 sm:h-6 sm:w-6 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                        settings.contactDailyDigestEnabled &&
                        settings.emailNotificationsEnabled
                          ? "translate-x-6 sm:translate-x-7"
                          : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Card 3: Test Email Dispatch & Verification */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-xs">
            <div className="flex items-start gap-3.5 mb-4">
              <span className="text-xl">✉️</span>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-[#041B44]">
                  {t("settings.testEmailTitle", "Test Email Delivery")}
                </h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                  {t(
                    "settings.testEmailDesc",
                    "Send a sample notification to verify that your SMTP credentials and mail servers are functioning properly."
                  )}
                </p>
              </div>
            </div>

            <form
              onSubmit={handleSendTestEmail}
              className="mt-4 flex flex-col sm:flex-row gap-3 max-w-xl"
            >
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder={t(
                  "settings.testEmailPlaceholder",
                  "Enter recipient email address..."
                )}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-hidden focus:border-[#037897] focus:ring-1 focus:ring-[#037897]"
                required
              />
              <button
                type="submit"
                disabled={sendingTest}
                className="bg-[#037897] hover:bg-[#025c74] text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-xs transition-colors disabled:opacity-60 flex items-center justify-center gap-2 shrink-0 cursor-pointer"
              >
                {sendingTest ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{t("settings.sending", "Sending...")}</span>
                  </>
                ) : (
                  <>
                    <span>🚀</span>
                    <span>{t("settings.sendTestButton", "Send Test Email")}</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
